import {
  Box,
  Button,
  Card,
  Icon,
  IconButton,
  Stack,
  Typography,
  Fade,
  Slide,
  useTheme,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { Cancel, Save, Delete, Lock, LockOpen } from "@mui/icons-material";
import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { AUTURA_ASPHALT_LIGHT, getHighlightColorFromHex } from "context/themeContext";
import { useTranslation } from "react-i18next";
import { saveUserSignature } from "api/file";
import { ACTION_TYPE, AppContext } from "context/appContext";
import { TOAST_TYPE,ToastContext } from "context/toastContext";

interface SignaturePadProps {
  setUILock?: (locked: boolean) => void;
  onSignatureCreated?: () => void;
}

const SignaturePad = ({setUILock, onSignatureCreated}: SignaturePadProps): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { state, dispatch } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [isEditing, setIsEditing] = useState(!state.signature);
  const [isUploading, setIsUploading] = useState(false);
  const [signatureURL, setSignatureURL] = useState<string | null>(state.signature?.url || null);

  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.width = canvasRef.current.parentElement?.clientWidth || 0;
      const context = canvasRef.current.getContext("2d");
      if (context) {
        context.fillStyle = "white";
        context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        context.lineWidth = 4;
        context.lineJoin = "round";
        context.lineCap = "round";
        context.strokeStyle = "black";
        setCtx(context);
      }
    }
  }, [isEditing]);

  const finishDrawing = () => {
    if (ctx) {
      ctx.closePath();
      setIsDrawing(false);
    }
  };
  const startDrawing = (
    event:
      | React.MouseEvent<HTMLCanvasElement, MouseEvent>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    const canvasPosition = event.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = "touches" in event ? event.touches[0] : event;
    const x = clientX - canvasPosition.left;
    const y = clientY - canvasPosition.top;
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    }
  };
  const draw = (
    event:
      | React.MouseEvent<HTMLCanvasElement, MouseEvent>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    const canvasPosition = event.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = "touches" in event ? event.touches[0] : event;
    const x = clientX - canvasPosition.left;
    const y = clientY - canvasPosition.top;
    if (!isDrawing) return;
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    setIsCanvasEmpty(false);
  };
  const clearCanvas = () => {
    if (ctx) {
      ctx.clearRect(0, 0, canvasRef.current?.width || 0, canvasRef.current?.height || 0);
      ctx.fillRect(0, 0, canvasRef.current?.width || 0, canvasRef.current?.height || 0);
    }
    setIsCanvasEmpty(true);
  };
  const startEditing = () => {
    setIsEditing(true);
    
    // Don't clear the canvas - preserve existing signature for editing
    // Only initialize canvas if it's truly empty
    if (ctx && isCanvasEmpty) {
      ctx.clearRect(0, 0, canvasRef.current?.width || 0, canvasRef.current?.height || 0);
      ctx.fillRect(0, 0, canvasRef.current?.width || 0, canvasRef.current?.height || 0);
    }
  };
  const cancelEditing = () => {
    setIsEditing(false);
    
    if (ctx) {
      ctx.clearRect(0, 0, canvasRef.current?.width || 0, canvasRef.current?.height || 0);
      ctx.fillRect(0, 0, canvasRef.current?.width || 0, canvasRef.current?.height || 0);
      
      // If there's a confirmed signature, restore it to the canvas
      if (signatureURL) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
        };
        img.src = signatureURL;
        setIsCanvasEmpty(false);
      } else {
        setIsCanvasEmpty(true);
      }
    }
  };

  const confirmSignature = async () => {
    if (!canvasRef.current || isCanvasEmpty || isUploading) return;
    
    const signatureData = canvasRef.current.toDataURL("image/png");
    setSignatureURL(signatureData);
    setIsEditing(false);
    setIsCanvasEmpty(false); // Keep the signature visible
    setIsUploading(true);
    
    // Lock the UI to prevent navigation
    if (setUILock) {
      setUILock(true);
    }
    
    try {
      // Convert base64 to blob
      const base64Data = signatureData.split(',')[1];
      const binaryData = atob(base64Data);
      const arrayBuffer = new ArrayBuffer(binaryData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([arrayBuffer], { type: 'image/png' });
      const file = new File([blob], 'signature.png', { type: 'image/png' });
      
      // Save signature using direct API call
      const response = await saveUserSignature(file);
      
      if (response.ok) {
        // Success - signature uploaded and saved
        const result = await response.json();
        // Update the signature in the app state
        dispatch({
          type: ACTION_TYPE.SET_SIGNATURE,
          payload: result
        });
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t("signature.saved", { ns: "userSettings" }),
        })
      } else {
        throw new Error("Failed to save signature");
      }
    } catch (error) {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t("signature.error", { ns: "userSettings" }),
      })
    } finally {
      setIsUploading(false);
      // Unlock the UI
      if (setUILock) {
        setUILock(false);
      }
      if (onSignatureCreated) {
        onSignatureCreated();
      }
    }
  };

  const height = useMediaQuery(useTheme().breakpoints.up('lg')) ? 150 : 75;

  return (
    <Card sx={{ border: `1px solid ${AUTURA_ASPHALT_LIGHT}`, bgcolor: "white", position: "relative", width: "100%"}} elevation={3} raised>
      <Box sx={{ position: "relative", height: height, width: "100%" }}>
        {isEditing ? (
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseUp={finishDrawing}
            onMouseMove={draw}
            onMouseLeave={finishDrawing}
            onTouchStart={startDrawing}
            onTouchEnd={finishDrawing}
            onTouchMove={draw}
            onTouchCancel={finishDrawing}
            height={height}
            style={{ 
              cursor: "crosshair",
              width: "100%",
              height: "100%",
              display: "block"
            }}
          />
        ) : (
          <Box sx={{ height: height, width: "100%", objectFit: "contain" }}>
            <img
              src={signatureURL || ""}
              alt="user signature"
              style={{
                height: height,
              }}
            />
          </Box>
        )}

        {/* Placeholder text */}
        {isCanvasEmpty && isEditing && (
          <Typography
            variant="h6"
            color={AUTURA_ASPHALT_LIGHT}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            {t("signature.placeholder_editing", { ns: "userSettings" })}
          </Typography>
        )}
      </Box>
      
      {/* Buttons - slide up/down when editing */}
      <Slide direction="up" in={isEditing} timeout={400}>
        <Stack
          direction="row"
          display="flex"
          justifyContent="center"
          alignContent="center"
          alignItems="center"
          p={0}
          height={40}
        >
          <Button
            onClick={clearCanvas}
            variant="outlined"
            sx={{ borderRadius: 0, flex: 1, height: "100%" }}
            disableElevation
            color="secondary"
            endIcon={<Icon component={Delete} />}
          >
            {t("clear")}
          </Button>
          <Button
            onClick={cancelEditing}
            variant="outlined"
            sx={{ borderRadius: 0, flex: 1, height: "100%" }}
            disableElevation
            color="secondary"
            endIcon={<Icon component={Cancel} />}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={confirmSignature}
            disableElevation
            sx={{ borderRadius: 0, flex: 1, height: "100%" }}
            variant="contained"
            color="secondary"
            endIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <Icon component={Save} />}
            disabled={isCanvasEmpty || isUploading}
          >
            {isUploading ? t("saving") : t("save")}
          </Button>
        </Stack>
      </Slide>

      {/* Loading overlay when uploading */}
      {isUploading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Overlay when not editing - covers entire card */}
      {!isEditing && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "auto",
          }}
        >
          <Fade in={!isEditing} timeout={400}>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 3,
              }}
            >
              <IconButton
                onClick={startEditing}
                disabled={isUploading}
                sx={{
                  color: "secondary.main",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: getHighlightColorFromHex(theme.palette.primary.main),
                  },
                }}
                size="small"
              >
                <Lock />
              </IconButton>
            </Box>
          </Fade>
        </Box>
      )}

      {/* Unlock button when editing */}
      <Fade in={isEditing} timeout={400}>
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 3,
          }}
        >
          <IconButton
            onClick={cancelEditing}
            disabled={isUploading}
            sx={{
              color: "secondary.main",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: getHighlightColorFromHex(theme.palette.primary.main),
              },
            }}
            size="small"
          >
            <LockOpen />
          </IconButton>
        </Box>
      </Fade>
    </Card>
  );
};

export default SignaturePad;
